import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import AnimatedTransition from '@/components/AnimatedTransition';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Mail, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const formSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [formError, setFormError] = useState('');

  // Removed useEffect that assumed status on AuthUser

  const [isSubmitting, setIsSubmitting] = useState(false);

  const projectSlug = window.location.pathname.split('/')[1];

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (values) => {
    setIsSubmitting(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });
    console.log("Supabase sign-in response:", { data, error });

    if (error) {
      console.error("Login error:", error);
      if (error.message.toLowerCase().includes('email not confirmed')) {
        setFormError('You must first click on the link in the email you received after signing up before signing in for the first time.');
      } else if (error.message.toLowerCase().includes('invalid login credentials') || error.message.toLowerCase().includes('invalid login')) {
        setFormError('The email or password you entered is incorrect.');
      } else {
        setFormError('Login failed: ' + error.message);
      }
      setIsSubmitting(false);
      return;
    }

    if (!data.session || !data.session.user) {
      setFormError('You must first click on the link in the email you received after signing up before signing in for the first time.');
      setIsSubmitting(false);
      return;
    }

    // Check user status from users table
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('status')
      .eq('id', data.session.user.id)
      .single();

    if (userError) {
      console.error('Failed to fetch user status:', userError.message);
      setFormError('Login failed: Unable to verify user status.');
      setIsSubmitting(false);
      return;
    }

    if (userData?.status === 'deactivated') {
      setFormError('Your account has been deactivated.');
      await supabase.auth.signOut();
      toast.error('Your account has been deactivated.');
      setIsSubmitting(false);
      return;
    }

    setFormError('');
    toast.success('Welcome back!', { description: `Signed in as ${values.email}` });
    navigate('/');
    setIsSubmitting(false);
  };

  useEffect(() => {
    const originalFavicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
    const existingHref = originalFavicon?.href;

    let favicon = document.querySelector("link[rel~='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('href', '/OS-favicon.png');
    document.title = "Official Scoring | Sign In";

    return () => {
      if (favicon && existingHref) {
        favicon.setAttribute('href', existingHref);
      }
    };
  }, []);

  return (
    <AnimatedTransition>
      <div className="container max-w-screen-sm py-12 space-y-6">
        <div className="flex items-center justify-center">
          <img
            src={
              projectSlug === 'gwent'
                ? '/gwent-logo.jpeg'
                : '/OfficialScoringMainLogo.jpeg'
            }
            alt="Official Scoring Logo"
            className="h-16 w-full max-w-lg object-contain"
          />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input className="pl-10" placeholder="you@example.com" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input className="pl-10" type="password" placeholder="******" {...field} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {formError && (
                  <div className="text-sm text-red-600 font-medium text-center">
                    {formError}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#30702e] hover:bg-[#2c5c33] text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </Form>
            <div className="mt-4 text-center">
              <Link
                to="/forgotpassword"
                className="text-sm font-semibold text-[#30702e] hover:underline"
              >
                Forgot your password?
              </Link>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-center">
          <Link to="/projects/standings" className="mt-4 flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img
              src="/lovable-uploads/d4c7df19-9c74-4ee3-8b7d-3585fbf47e66.png"
              alt="Gwent Golf Union"
              className="h-10 w-10 object-contain"
            />
            <span className="text-sm font-medium text-[#30702e]">Navigate to Gwent Golf Union</span>
          </Link>
        </div>
      </div>
    </AnimatedTransition>
  );
};

export default Login;